<script setup lang="ts">
import { ComputedRef, computed } from 'vue'
import type { StudentProgress } from '../../models/Activity'
import { KsExpandItem, KsProgress } from '@aschehoug/kloss'
import ActivityAssignmentAnswer from './ActivityAssignmentAnswer.vue'
import { useI18n } from '../../composables/useI18n'

const { t } = useI18n()

const props = defineProps<{
  studentProgress: StudentProgress
}>()

const isDisabled = computed(() => {
  if (typeof props.studentProgress.lastInteraction === 'undefined') {
    return false
  }

  return !(props.studentProgress.lastInteraction.length > 0)
})

const shortenedStudentName = computed(() =>
  `${props.studentProgress.user.firstName} ${props.studentProgress.user.lastName.charAt(0)}`)

const lastInteractionText: ComputedRef<string> = computed(() => {
  if (typeof props.studentProgress.lastInteraction === 'undefined') {
    return ''
  }
  return props.studentProgress.lastInteraction
})

const progressText: ComputedRef<string> = computed(() => {
  if (lastInteractionText.value.length === 0) {
    return ''
  }

  if (typeof props.studentProgress.filledFields === 'undefined'
    || typeof props.studentProgress.totalFields === 'undefined'
  ) {
    return ''
  }

  return t('activity.messages.progressText', {
      filledFields: props.studentProgress.filledFields,
      totalFields: props.studentProgress.totalFields,
    })
})
</script>

<template>
  <KsExpandItem
    :key="props.studentProgress.user.userId"
    class="white-expand"
    :title="t('activity.messages.activityForStudent', { student: props.studentProgress.user.fullName})"
    :disabled="isDisabled"
  >
    <template #toggle>
      <div class="grid w-full grid-cols-12 items-center gap-4">
        <p
          class="col-span-4 overflow-x-hidden text-ellipsis whitespace-nowrap text-left sm:col-span-3"
          v-text="shortenedStudentName"
        />
        <KsProgress
          class="col-span-8 sm:col-span-5"
          :max="props.studentProgress.totalFields || 0"
          :values="[props.studentProgress.filledFields || 0]"
        />
        <p
          class="col-span-2 hidden text-left text-sm sm:block"
          v-text="progressText"
        />
        <p
          class="col-span-2 hidden text-left text-sm sm:block"
          v-text="lastInteractionText"
        />
      </div>
    </template>
    <template #content>
      <ActivityAssignmentAnswer
        :assignmentAnswer="props.studentProgress.assignmentAnswer"
        :url="props.studentProgress.url"
        :userId="props.studentProgress.user.userId"
      />
    </template>
  </KsExpandItem>
</template>
